import { Check, CirclePlay, X as CloseIcon } from "lucide-react";

import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  IgAzurePrimitives,
  IgBasePrimitives,
  IgCoreTextColors,
  IgGreenPrimitives,
  IgInteractiveTextColors,
  IgTonalBackgroundColors,
} from "@intelligems/ig-design-tokens";
import { IgButton, IgChip, IgTypography } from "@intelligems/ig-theme";

import {
  SMART_CHECKOUT_ID,
  SMART_MERCHANDISING_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_SHIPPING_ID,
  UNLIMITED_ID,
} from "@src/constants/billing";
import type { PricingGatedModalViewProps } from "./PricingGatedModal.view.types";
import { GateTrialExclusionChip } from "./GateTrialExclusionChip";
import GatedSmartCheckoutPng from "./images/gated-smart-checkout.png";
import GatedSmartMerchandisingPng from "./images/gated-smart-merchandising.png";
import GatedSmartPricingPng from "./images/gated-smart-pricing.png";
import GatedSmartShippingPng from "./images/gated-smart-shipping.png";
import GatedUnlimitedPng from "./images/gated-unlimited.png";

function getModuleImage(moduleId: string): string | undefined {
  return (
    {
      [SMART_CHECKOUT_ID]: GatedSmartCheckoutPng,
      [SMART_MERCHANDISING_ID]: GatedSmartMerchandisingPng,
      [SMART_PRICING_LIMITED_ID]: GatedSmartPricingPng,
      [SMART_PRICING_UNLIMITED_ID]: GatedSmartPricingPng,
      [SMART_SHIPPING_ID]: GatedSmartShippingPng,
      [UNLIMITED_ID]: GatedUnlimitedPng,
    } as Record<string, string>
  )[moduleId];
}

type PricingGatedModalContentProps = Omit<
  PricingGatedModalViewProps,
  "open" | "orderVolumeTier" | "userState" | "videoPlaybackId"
> & {
  imageMaxHeight?: number;
  imageSx?: SxProps<Theme>;
  onViewDemo?: () => void;
  showCloseButton?: boolean;
};

export const PricingGatedModalContent = ({
  buttons,
  gateModuleDetails,
  imageMaxHeight,
  imageSx,
  moduleId,
  onClose,
  onViewDemo,
  showCloseButton = true,
  showInlineViewDemo,
}: PricingGatedModalContentProps) => {
  const {
    description,
    discount,
    features,
    isExcludedFromTrial,
    modules,
    name,
    title,
  } = gateModuleDetails;
  const moduleImage = getModuleImage(moduleId);

  const sharedChipSx = {
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "5%",
    p: "4px 10px",
    textTransform: "uppercase",
  };

  return (
    <>
      {showCloseButton && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
            zIndex: 1,
          })}
        >
          <CloseIcon size={24} />
        </IconButton>
      )}
      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {moduleImage && (
            <Stack width={"50%"} sx={{ position: "relative" }}>
              <Box
                component="img"
                aria-hidden
                src={moduleImage}
                alt={`Image for ${title}`}
                sx={[
                  {
                    maxHeight: imageMaxHeight,
                    maxWidth: "100%",
                    objectFit: "cover",
                  },
                  ...(Array.isArray(imageSx)
                    ? imageSx
                    : imageSx
                      ? [imageSx]
                      : []),
                ]}
              />
            </Stack>
          )}
          <Stack width={moduleImage ? "50%" : "100%"} sx={{ p: "16px 24px" }}>
            <Grid container alignItems={"center"} flexGrow={1}>
              <Box sx={{ px: 6 }}>
                <Stack gap={4}>
                  <Grid container gap={2}>
                    {isExcludedFromTrial ? (
                      <GateTrialExclusionChip />
                    ) : (
                      <>
                        <IgChip
                          color="secondary"
                          label={name}
                          sx={{
                            ...sharedChipSx,
                            backgroundColor: IgBasePrimitives[50],
                            borderColor: IgBasePrimitives[50],
                            color: IgBasePrimitives[500],
                          }}
                        />
                        {discount && (
                          <IgChip
                            color="secondary"
                            label={`Save ${discount}%`}
                            sx={{
                              ...sharedChipSx,
                              backgroundColor: IgGreenPrimitives[200],
                              borderColor: IgGreenPrimitives[200],
                              color: IgGreenPrimitives["A300"],
                            }}
                          />
                        )}
                      </>
                    )}
                  </Grid>
                  <Stack gap={2}>
                    <IgTypography
                      color={"primary"}
                      fontSize={"16px"}
                      fontWeight={600}
                    >
                      {title}
                    </IgTypography>
                    <IgTypography
                      variant="body1"
                      color={"textSecondary"}
                      fontSize={"12px"}
                      fontWeight={400}
                      lineHeight={"150%"}
                    >
                      {description}
                    </IgTypography>
                    <Stack gap={2}>
                      {features &&
                        features.length > 0 &&
                        features.map((feature) => (
                          <Stack
                            key={feature}
                            direction={"row"}
                            alignItems={"flex-start"}
                            gap={2}
                          >
                            <Box
                              sx={{ flexShrink: 0, lineHeight: 0, pt: "3px" }}
                            >
                              <Check color={IgBasePrimitives[500]} size={16} />
                            </Box>
                            <IgTypography
                              color={"textSecondary"}
                              fontSize={"14px"}
                              fontWeight={400}
                              lineHeight={"150%"}
                            >
                              {feature}
                            </IgTypography>
                          </Stack>
                        ))}
                    </Stack>
                    {showInlineViewDemo && (
                      <Box>
                        <IgButton
                          data-testid="view-demo-inline"
                          onClick={onViewDemo}
                          startIcon={<CirclePlay size={16} />}
                          sx={{
                            backgroundColor: IgTonalBackgroundColors.info,
                            borderRadius: "6px",
                            color: IgCoreTextColors.accent,
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: 1.5,
                            px: "12px",
                            py: "8px",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: IgAzurePrimitives[200],
                              color: IgInteractiveTextColors.accent.hover,
                            },
                          }}
                        >
                          View demo
                        </IgButton>
                      </Box>
                    )}
                    <Box>
                      {modules &&
                        modules.length > 0 &&
                        modules.map((module) => (
                          <IgChip
                            key={module}
                            color="secondary"
                            label={module}
                            sx={{
                              backgroundColor: IgBasePrimitives[50],
                              borderColor: IgBasePrimitives[50],
                              color: IgBasePrimitives[500],
                              fontSize: "11px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                            }}
                          />
                        ))}
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid container gap={2} width={"100%"} justifyContent={"flex-end"}>
              {!showInlineViewDemo && onViewDemo && (
                <Button
                  startIcon={<CirclePlay />}
                  variant="outlined"
                  onClick={onViewDemo}
                  sx={{
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "120%",
                  }}
                >
                  View demo
                </Button>
              )}
              {buttons.map(({ label, onClick, sx, variant }, index) => (
                <Button
                  key={label + index}
                  variant={variant}
                  onClick={onClick}
                  sx={sx}
                >
                  {label}
                </Button>
              ))}
            </Grid>
          </Stack>
        </Grid>
      </DialogContent>
    </>
  );
};
